import React, { useCallback } from 'react';
import Editor from '@monaco-editor/react';
import { PlayIcon, CheckIcon, RefreshIcon } from './Icons';

/* ── Monaco warm theme definitions ──────────────────────────────────────────
   Registered once on beforeMount. Approximations of the oklch() tokens in
   hex values that Monaco accepts.

   Light (ib-warm-light):  paper bg #f7f2e8, warm brown text
   Dark  (ib-warm-dark):   deep brown bg #181410, cream text
──────────────────────────────────────────────────────────────────────────── */

export function defineWarmThemes(monaco) {
  monaco.editor.defineTheme('ib-warm-light', {
    base: 'vs',
    inherit: true,
    rules: [
      { token: '',           foreground: '1f1a14' },
      { token: 'comment',    foreground: 'a89e8f', fontStyle: 'italic' },
      { token: 'keyword',    foreground: '6b3fbf', fontStyle: 'bold' },
      { token: 'string',     foreground: '3a7a4e' },
      { token: 'number',     foreground: '8c6a20' },
      { token: 'type',       foreground: '6b3fbf' },
      { token: 'function',   foreground: '1a5a8a' },
      { token: 'variable',   foreground: '1f1a14' },
      { token: 'operator',   foreground: '7a6050' },
      { token: 'delimiter',  foreground: '9a8f82' },
    ],
    colors: {
      'editor.background':                  '#f7f2e8',
      'editor.foreground':                  '#1f1a14',
      'editorLineNumber.foreground':        '#c0b8ae',
      'editorLineNumber.activeForeground':  '#6b6055',
      'editor.lineHighlightBackground':     '#ede8de80',
      'editor.selectionBackground':         '#b0a0e030',
      'editorCursor.foreground':            '#7048c0',
      'editorBracketMatch.background':      '#b0a0e020',
      'editorBracketMatch.border':          '#7048c0',
      'editorIndentGuide.background1':      '#e0d8cc',
      'editorIndentGuide.activeBackground1':'#c8bdb0',
      'editor.selectionHighlightBackground':'#b0a0e018',
      'editorWhitespace.foreground':        '#d8d0c4',
      'scrollbarSlider.background':         '#9a8f8230',
      'scrollbarSlider.hoverBackground':    '#9a8f8248',
      'scrollbarSlider.activeBackground':   '#9a8f8260',
    },
  });

  monaco.editor.defineTheme('ib-warm-dark', {
    base: 'vs-dark',
    inherit: true,
    rules: [
      { token: '',           foreground: 'f0e8db' },
      { token: 'comment',    foreground: '776c5d', fontStyle: 'italic' },
      { token: 'keyword',    foreground: 'a880e8', fontStyle: 'bold' },
      { token: 'string',     foreground: '5aad7a' },
      { token: 'number',     foreground: 'c8a850' },
      { token: 'type',       foreground: 'a880e8' },
      { token: 'function',   foreground: '60aadc' },
      { token: 'variable',   foreground: 'f0e8db' },
      { token: 'operator',   foreground: 'a89e8f' },
      { token: 'delimiter',  foreground: '776c5d' },
    ],
    colors: {
      'editor.background':                  '#181410',
      'editor.foreground':                  '#f0e8db',
      'editorLineNumber.foreground':        '#4a4035',
      'editorLineNumber.activeForeground':  '#776c5d',
      'editor.lineHighlightBackground':     '#221e1880',
      'editor.selectionBackground':         '#7050b840',
      'editorCursor.foreground':            '#9870d8',
      'editorBracketMatch.background':      '#7050b820',
      'editorBracketMatch.border':          '#9870d8',
      'editorIndentGuide.background1':      '#2a2420',
      'editorIndentGuide.activeBackground1':'#3a3028',
      'editor.selectionHighlightBackground':'#7050b818',
      'editorWhitespace.foreground':        '#2e2820',
      'scrollbarSlider.background':         '#776c5d30',
      'scrollbarSlider.hoverBackground':    '#776c5d48',
      'scrollbarSlider.activeBackground':   '#776c5d60',
    },
  });
}

/* ── Language badge ──────────────────────────────────────────────────────── */

function LanguageBadge({ language }) {
  const isPython = language === 'python';
  return (
    <span className="lang-badge" title={isPython ? 'Python' : 'Java'}>
      <svg className="lang-badge-icon" width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">
        <rect width="14" height="14" rx="3" fill={isPython ? '#3776AB' : '#E76F00'} />
        <polygon points="14,0 14,14 0,14" fill={isPython ? '#FFD43B' : '#5382A1'} />
      </svg>
      <span className="lang-badge-name">{isPython ? 'Python' : 'Java'}</span>
    </span>
  );
}

/* ── Traffic lights ──────────────────────────────────────────────────────── */

function TrafficLights() {
  return (
    <div className="traffic-lights" aria-hidden="true">
      <span className="tl tl-red"   />
      <span className="tl tl-amber" />
      <span className="tl tl-green" />
    </div>
  );
}

/* ── Test count display ──────────────────────────────────────────────────── */

function TestCount({ exercise, testResults }) {
  const total  = exercise?.test_cases?.length || 0;
  const passed = Object.values(testResults).filter(r => r?.passed).length;
  const tested = Object.keys(testResults).length > 0;
  if (!tested || total === 0) return null;
  return (
    <span className="action-tests">
      <CheckIcon size={11} />
      <strong className={passed === total ? 'tests-all-pass' : 'tests-partial'}>
        {passed}/{total}
      </strong>
    </span>
  );
}

/* ── EditorPanel ─────────────────────────────────────────────────────────── */

export default function EditorPanel({
  exercise,
  code,
  onChange,
  language,
  theme,
  onRun,
  onRunAndCheck,
  onReset,
  onEditorMount,
  attempts,
  testResults,
  isRunning,
  loading,
  isExplore,
}) {
  const monacoTheme = theme === 'dark' ? 'ib-warm-dark' : 'ib-warm-light';
  const filename    = language === 'java' ? 'Main.java' : 'main.py';
  const busy        = isRunning || loading;

  const handleBeforeMount = useCallback((monaco) => {
    defineWarmThemes(monaco);
  }, []);

  return (
    <div className="card editor-card">

      {/* ── Card header ─────────────────────────────────────── */}
      <div className="editor-header card-header">
        <TrafficLights />
        <span className="editor-filename">{filename}</span>
        <LanguageBadge language={language} />
        <span className="editor-meta">UTF-8 · LF · 4 spaces</span>
      </div>

      {/* ── Monaco editor ────────────────────────────────────── */}
      <div className="editor-monaco-wrap">
        <Editor
          height="100%"
          language={language}
          value={code}
          onChange={(v) => onChange(v || '')}
          theme={monacoTheme}
          beforeMount={handleBeforeMount}
          options={{
            fontFamily:           '"JetBrains Mono", "Cascadia Code", "Fira Code", Consolas, "Courier New", monospace',
            fontLigatures:        true,
            fontSize:             13,
            lineHeight:           Math.round(1.65 * 13),
            minimap:              { enabled: false },
            scrollBeyondLastLine: false,
            wordWrap:             'off',
            automaticLayout:      true,
            padding:              { top: 14, bottom: 14 },
            smoothScrolling:      true,
            cursorBlinking:       'smooth',
            renderLineHighlight:  'line',
          }}
          onMount={onEditorMount}
        />
      </div>

      {/* ── Action bar ───────────────────────────────────────── */}
      <div className="action-bar">

        {/* Left: Reset */}
        <button
          className="action-btn action-reset"
          onClick={onReset}
          disabled={!exercise}
          title="Reset to starter code"
        >
          <RefreshIcon size={12} />
          Reset
        </button>

        {/* Centre: stats */}
        <div className="action-stats">
          {attempts > 0 && (
            <span className="action-attempt">
              Attempt <strong>{attempts}</strong>
            </span>
          )}
          <TestCount exercise={exercise} testResults={testResults} />
        </div>

        {/* Right: Run (secondary) + Submit/Run & Check (primary) — same layout for all exercise types.
             Explore exercises use the same two-button pattern so students must explicitly
             submit after making the instructed code change; Run alone never marks complete. */}
        <div className="action-run-group">
          <button
            className="action-btn action-run-secondary"
            onClick={onRun}
            disabled={busy || !exercise}
            title="Run code in terminal (no grading)"
          >
            {isRunning ? (
              <>⏳ Running…</>
            ) : (
              <>
                <PlayIcon size={10} />
                Run
              </>
            )}
          </button>

          <button
            className="action-btn action-run-primary"
            onClick={onRunAndCheck}
            disabled={busy || !exercise}
            title={isExplore ? 'Submit your changes' : 'Run and grade (Ctrl+Enter)'}
          >
            {loading ? (
              <>⏳ Checking…</>
            ) : (
              <>
                <CheckIcon size={12} />
                Run & Submit
              </>
            )}
          </button>
        </div>

      </div>

    </div>
  );
}
